(() => {
  const config = window.KobuRetailerBonusCartConfig || {};
  const bonusProperty = '_kobu_retailer_bonus';
  const bonusSourceProperty = '_kobu_retailer_bonus_source';
  const bonusSourceValue = 'claim';
  const freeShippingDefinition =
    config.freeShipping && Number(config.freeShipping.threshold || 0) > 0
      ? {
          type: config.freeShipping.type || 'shipping',
          label: config.freeShipping.label || 'Free shipping',
          threshold: Number(config.freeShipping.threshold || 0),
          attributeName: config.freeShipping.attributeName || '_kobu_free_shipping_claimed',
          discountCode: config.freeShipping.discountCode || 'RETAILER500',
        }
      : null;

  const getCartUrl = () => {
    if (window.routes?.cart_url) return `${window.routes.cart_url}.js`;
    return '/cart.js';
  };

  const getRequestConfig = () =>
    typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        };

  const normalizePropertyValue = (value) => {
    if (value == null) return '';
    return String(value);
  };

  const getLineProperty = (item, propertyName) => {
    const properties = item?.properties;
    if (!properties) return '';

    if (Array.isArray(properties)) {
      const match = properties.find((property) => {
        if (Array.isArray(property)) return property[0] === propertyName;
        return property?.name === propertyName || property?.first === propertyName;
      });

      if (!match) return '';
      if (Array.isArray(match)) return normalizePropertyValue(match[1]);
      return normalizePropertyValue(match.value ?? match.last);
    }

    return normalizePropertyValue(properties[propertyName]);
  };

  const getCartAttribute = (cart, attributeName) => normalizePropertyValue(cart?.attributes?.[attributeName]);

  const fetchCart = () =>
    fetch(getCartUrl(), { headers: { Accept: 'application/json' } }).then((response) => response.json());

  const postCart = (url, body) => {
    const requestConfig = getRequestConfig();
    requestConfig.body = JSON.stringify(body);

    return fetch(url, requestConfig).then((response) => response.json());
  };

  const getSectionInnerHTML = (html, selector = '.shopify-section') =>
    new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';

  const getCartUpdateSections = () => {
    const sections = new Set();
    const cartDrawer = document.querySelector('cart-drawer');
    const cartItems = document.querySelector('cart-items');

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      cartDrawer.getSectionsToRender().forEach((section) => {
        if (section.id) sections.add(section.id);
      });
    }

    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      cartItems.getSectionsToRender().forEach((section) => {
        if (section.section) sections.add(section.section);
      });
    }

    return Array.from(sections);
  };

  const addCartUpdateSections = (body) => {
    const sections = getCartUpdateSections();
    if (!sections.length) return body;

    return {
      ...body,
      sections,
      sections_url: window.location.pathname,
    };
  };

  const renderCartUpdateSections = (response, options = {}) => {
    if (!response?.sections) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (
      cartDrawer &&
      response.sections['cart-drawer'] &&
      typeof cartDrawer.renderContents === 'function' &&
      (options.openDrawer || cartDrawer.classList.contains('active'))
    ) {
      cartDrawer.renderContents({
        ...response,
        id: response.id || 'retailer-free-shipping',
      });
    }

    const cartItems = document.querySelector('cart-items');
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return;

    cartItems.getSectionsToRender().forEach((section) => {
      const sectionHtml = response.sections[section.section];
      const container = document.getElementById(section.id);
      if (!sectionHtml || !container) return;

      const elementToReplace = container.querySelector(section.selector) || container;
      elementToReplace.innerHTML = getSectionInnerHTML(sectionHtml, section.selector);
    });
  };

  const bonusDefinitions = Object.entries(config.bonuses || {})
    .map(([type, bonus]) => ({
      type,
      label: bonus.label || type,
      threshold: Number(bonus.threshold || 0),
      handle: bonus.handle || '',
      variantId: bonus.variantId ? String(bonus.variantId) : '',
      directAdd: Boolean(bonus.directAdd),
    }))
    .filter((bonus) => bonus.threshold >= 0 && (bonus.variantId || bonus.handle));

  const qualifyingVariantIds = new Set((config.qualifyingVariantIds || []).map((id) => String(id)));
  let bonusVariantIds = new Set(bonusDefinitions.filter((bonus) => bonus.variantId).map((bonus) => bonus.variantId));
  let bonusResolutionPromise;

  const getBonusForVariant = (variantId) =>
    bonusDefinitions.find((bonus) => bonus.variantId && bonus.variantId === String(variantId || ''));

  const isBonusLine = (item, bonusType) => {
    const lineBonusType = getLineProperty(item, bonusProperty);
    const variantBonus = getBonusForVariant(item?.variant_id);

    if (bonusType) {
      return lineBonusType === bonusType || variantBonus?.type === bonusType;
    }

    return Boolean(lineBonusType) || Boolean(variantBonus);
  };

  const getQualifyingSubtotal = (cart) =>
    (cart.items || []).reduce((subtotal, item) => {
      const variantId = String(item.variant_id || '');
      if (isBonusLine(item)) return subtotal;
      if (bonusVariantIds.has(variantId)) return subtotal;
      if (qualifyingVariantIds.size && !qualifyingVariantIds.has(variantId)) return subtotal;

      return subtotal + Number(item.original_line_price ?? item.final_line_price ?? item.line_price ?? 0);
    }, 0);

  const isFreeShippingClaimed = (cart, subtotal = getQualifyingSubtotal(cart)) =>
    Boolean(
      freeShippingDefinition &&
        subtotal >= freeShippingDefinition.threshold
    );

  const resolveBonusDefinitions = () => {
    if (bonusResolutionPromise) return bonusResolutionPromise;

    const unresolvedBonuses = bonusDefinitions.filter((bonus) => !bonus.variantId && bonus.handle);

    bonusResolutionPromise = Promise.all(
      unresolvedBonuses.map((bonus) =>
        fetch(`/products/${bonus.handle}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((product) => {
            const variant = product?.variants?.find((candidate) => candidate.available) || product?.variants?.[0];
            if (variant?.id) {
              bonus.variantId = String(variant.id);
            }
          })
          .catch(() => {})
      )
    ).then(() => {
      bonusVariantIds = new Set(bonusDefinitions.filter((bonus) => bonus.variantId).map((bonus) => bonus.variantId));
      return bonusDefinitions.filter((bonus) => bonus.variantId);
    });

    return bonusResolutionPromise;
  };

  const getBonusLines = (cart, bonus) =>
    (cart.items || []).filter((item) => {
      if (getLineProperty(item, bonusProperty) === bonus.type) return true;
      return String(item.variant_id || '') === bonus.variantId;
    });

  const getBonusStates = (cart) => {
    const subtotal = getQualifyingSubtotal(cart);

    const states = bonusDefinitions.reduce((accumulatedStates, bonus) => {
      const lines = getBonusLines(cart, bonus);

      accumulatedStates[bonus.type] = {
        type: bonus.type,
        label: bonus.label,
        threshold: bonus.threshold,
        remaining: Math.max(0, bonus.threshold - subtotal),
        qualified: subtotal >= bonus.threshold,
        inCart: lines.length > 0,
        quantity: lines.reduce((quantity, line) => quantity + Number(line.quantity || 0), 0),
      };

      return accumulatedStates;
    }, {});

    if (freeShippingDefinition) {
      const claimed = isFreeShippingClaimed(cart, subtotal);

      states[freeShippingDefinition.type] = {
        type: freeShippingDefinition.type,
        label: freeShippingDefinition.label,
        threshold: freeShippingDefinition.threshold,
        remaining: Math.max(0, freeShippingDefinition.threshold - subtotal),
        qualified: subtotal >= freeShippingDefinition.threshold,
        claimed,
        inCart: claimed,
        quantity: claimed ? 1 : 0,
      };
    }

    return states;
  };

  const publishCartUpdate = (cart) => {
    updateBonusProductLinks(cart);

    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'retailer-bonus-cart',
      cartData: cart,
    });
  };

  const getBonusHandlesInCart = (cart) =>
    new Set(
      (cart.items || [])
        .map((item) => {
          const bonus = getBonusForVariant(item.variant_id);
          return bonus && isBonusLine(item, bonus.type) ? bonus.handle : '';
        })
        .filter(Boolean)
    );

  const preventDisabledBonusLink = (event) => {
    const link = event.currentTarget;
    if (link.getAttribute('aria-disabled') !== 'true') return;

    event.preventDefault();
  };

  const updateBonusProductLinks = (cart) => {
    const handlesInCart = getBonusHandlesInCart(cart);

    document.querySelectorAll('[data-retailer-free-bonus-link]').forEach((link) => {
      const handle = link.dataset.retailerFreeBonusLink || '';
      const shouldDisable = handlesInCart.has(handle);

      if (!link.dataset.retailerFreeBonusHref && link.hasAttribute('href')) {
        link.dataset.retailerFreeBonusHref = link.getAttribute('href');
      }

      if (shouldDisable) {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.classList.add('card__link--disabled');
      } else if (link.dataset.retailerFreeBonusHref) {
        link.setAttribute('href', link.dataset.retailerFreeBonusHref);
        link.removeAttribute('aria-disabled');
        link.classList.remove('card__link--disabled');
      }

      link.removeEventListener('click', preventDisabledBonusLink);
      link.addEventListener('click', preventDisabledBonusLink);
    });
  };

  const renderCartAddSections = (response) => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && response?.sections && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.classList.remove('is-empty');
      cartDrawer.renderContents(response);
    }
  };

  const normalizeBonusQuantity = async (cart, bonus) => {
    const lines = getBonusLines(cart, bonus);
    if (!lines.length || !window.routes?.cart_update_url) return cart;

    const updates = {};

    lines.forEach((line, index) => {
      const quantity = index === 0 ? 1 : 0;
      if (Number(line.quantity || 0) !== quantity) {
        updates[line.key] = quantity;
      }
    });

    if (!Object.keys(updates).length) return cart;

    await postCart(window.routes.cart_update_url, { updates });
    const updatedCart = await fetchCart();
    publishCartUpdate(updatedCart);

    return updatedCart;
  };

  const setFreeShippingClaimed = async (claimed, options = {}) => {
    if (!freeShippingDefinition) {
      throw new Error('Free shipping bonus is unavailable.');
    }

    const response = await postCart(window.routes?.cart_update_url || '/cart/update.js', addCartUpdateSections({
      attributes: {
        [freeShippingDefinition.attributeName]: claimed ? 'true' : '',
      },
    }));

    renderCartUpdateSections(response, { openDrawer: Boolean(options.openDrawer) });

    const updatedCart = await fetchCart();
    publishCartUpdate(updatedCart);

    return updatedCart;
  };

  let syncInFlight = false;
  let syncQueued = false;

  const syncBonuses = async (cartOverride) => {
    if (syncInFlight) {
      syncQueued = true;
      return;
    }

    syncInFlight = true;

    try {
      const resolvedBonuses = await resolveBonusDefinitions();
      if (!resolvedBonuses.length && !freeShippingDefinition) return;

      const cart = cartOverride && Array.isArray(cartOverride.items) ? cartOverride : await fetchCart();
      const subtotal = getQualifyingSubtotal(cart);
      const updates = {};
      const attributes = {};

      resolvedBonuses.forEach((bonus) => {
        const lines = getBonusLines(cart, bonus);
        if (!lines.length) return;

        if (subtotal < bonus.threshold) {
          lines.forEach((line) => {
            updates[line.key] = 0;
          });
          return;
        }

        lines.forEach((line, index) => {
          const quantity = index === 0 ? 1 : 0;
          if (Number(line.quantity || 0) !== quantity) {
            updates[line.key] = quantity;
          }
        });
      });

      if (freeShippingDefinition) {
        const shouldHaveFreeShipping = subtotal >= freeShippingDefinition.threshold;
        const hasFreeShipping = getCartAttribute(cart, freeShippingDefinition.attributeName) === 'true';

        if (shouldHaveFreeShipping !== hasFreeShipping) {
          attributes[freeShippingDefinition.attributeName] = shouldHaveFreeShipping ? 'true' : '';
        }
      }

      const body = {};
      if (Object.keys(updates).length) body.updates = updates;
      if (Object.keys(attributes).length) body.attributes = attributes;

      if (!Object.keys(body).length) return cart;

      const response = await postCart(window.routes?.cart_update_url || '/cart/update.js', addCartUpdateSections(body));
      renderCartUpdateSections(response);
      const updatedCart = await fetchCart();
      publishCartUpdate(updatedCart);

      return updatedCart;
    } catch (error) {
      console.error('Kobu retailer bonus cart cleanup failed', error);
    } finally {
      syncInFlight = false;

      if (syncQueued) {
        syncQueued = false;
        syncBonuses();
      }
    }
  };

  const getInvalidBonusUpdates = (cart) => {
    const subtotal = getQualifyingSubtotal(cart);
    const updates = {};

    bonusDefinitions.forEach((bonus) => {
      if (subtotal >= bonus.threshold) return;

      getBonusLines(cart, bonus).forEach((line) => {
        updates[line.key] = 0;
      });
    });

    if (
      freeShippingDefinition &&
      subtotal < freeShippingDefinition.threshold &&
      getCartAttribute(cart, freeShippingDefinition.attributeName) === 'true'
    ) {
      return {
        updates,
        attributes: {
          [freeShippingDefinition.attributeName]: '',
        },
      };
    }

    return { updates, attributes: {} };
  };

  const hasInvalidBonus = (cart) => {
    const { updates, attributes } = getInvalidBonusUpdates(cart);
    return Object.keys(updates).length > 0 || Object.keys(attributes).length > 0;
  };

  // Checkout is a separate request from cart cleanup. Revalidate immediately
  // before submitting so a slow connection cannot preserve an under-qualified
  // free bonus in the checkout session.
  const settleBonusesBeforeCheckout = async () => {
    await resolveBonusDefinitions();

    const pendingMutations = Array.from(window.KobuPendingCartMutations || []);
    if (pendingMutations.length) {
      await Promise.all(pendingMutations.map((mutation) => Promise.resolve(mutation).catch(() => undefined)));
    }

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const cart = await fetchCart();
      const { updates, attributes } = getInvalidBonusUpdates(cart);

      if (!Object.keys(updates).length && !Object.keys(attributes).length) {
        return cart;
      }

      const body = addCartUpdateSections({ updates, attributes });
      const response = await postCart(window.routes?.cart_update_url || '/cart/update.js', body);
      renderCartUpdateSections(response);
      publishCartUpdate(await fetchCart());
    }

    const finalCart = await fetchCart();
    if (hasInvalidBonus(finalCart)) {
      throw new Error('The cart changed. Please review it before checkout.');
    }

    return finalCart;
  };

  let checkoutGuardInFlight = false;

  const runCheckoutGuard = async (form, submitter) => {
    if (checkoutGuardInFlight) return;

    checkoutGuardInFlight = true;
    if (submitter) {
      submitter.disabled = true;
      submitter.setAttribute('aria-busy', 'true');
    }

    try {
      await settleBonusesBeforeCheckout();
      // Cart section rendering can replace the original form while cleanup is
      // in progress. Navigate using the live cart instead of submitting the
      // now-detached form reference.
      window.location.assign('/checkout');
    } catch (error) {
      console.error('Kobu checkout bonus validation failed', error);
      window.alert('Your cart changed. Please review your cart before checkout.');
      if (submitter) {
        submitter.disabled = false;
        submitter.removeAttribute('aria-busy');
      }
      checkoutGuardInFlight = false;
    }
  };

  const handleCheckoutClick = (event) => {
    const submitter = event.target.closest?.('[name="checkout"]');
    if (!submitter || submitter.disabled) return;

    const formId = submitter.getAttribute('form');
    const form = (formId && document.getElementById(formId)) || submitter.form;
    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    runCheckoutGuard(form, submitter);
  };

  const handleCheckoutSubmit = (event) => {
    const submitter = event.submitter;
    if (
      event.defaultPrevented ||
      (submitter && submitter.name !== 'checkout') ||
      (!submitter && !event.target.querySelector('[name="checkout"]')) ||
      checkoutGuardInFlight
    ) {
      return;
    }

    event.preventDefault();
    runCheckoutGuard(event.target, submitter);
  };

  const claimBonus = async (type) => {
    if (freeShippingDefinition && type === freeShippingDefinition.type) {
      const cart = await fetchCart();
      const subtotal = getQualifyingSubtotal(cart);

      if (subtotal < freeShippingDefinition.threshold) {
        return { status: 'locked', cart };
      }

      if (isFreeShippingClaimed(cart, subtotal)) {
        return { status: 'already_claimed', cart };
      }

      const updatedCart = await setFreeShippingClaimed(true, { openDrawer: true });
      return { status: 'claimed', cart: updatedCart };
    }

    const resolvedBonuses = await resolveBonusDefinitions();
    const bonus = resolvedBonuses.find((candidate) => candidate.type === type);

    if (!bonus?.variantId) {
      throw new Error(`Retailer bonus variant is unavailable for ${type}`);
    }

    const cart = await fetchCart();
    const subtotal = getQualifyingSubtotal(cart);
    const existingLines = getBonusLines(cart, bonus);

    if (subtotal < bonus.threshold) {
      return { status: 'locked', cart };
    }

    if (existingLines.length) {
      const updatedCart = await normalizeBonusQuantity(cart, bonus);
      return { status: 'already_claimed', cart: updatedCart };
    }

    const body = {
      items: [
        {
          id: Number(bonus.variantId),
          quantity: 1,
          properties: {
            [bonusProperty]: bonus.type,
            [bonusSourceProperty]: bonusSourceValue,
          },
        },
      ],
    };

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    const response = await postCart(window.routes.cart_add_url || '/cart/add.js', body);
    renderCartAddSections(response);

    const updatedCart = await fetchCart();
    publishCartUpdate(updatedCart);

    return { status: 'claimed', cart: updatedCart };
  };

  const prepareProductAdd = async (formDataOrVariantId) => {
    const variantId = formDataOrVariantId instanceof FormData ? formDataOrVariantId.get('id') : formDataOrVariantId;
    if (!variantId) return { allowed: true };

    const resolvedBonuses = await resolveBonusDefinitions();
    const bonus = resolvedBonuses.find((candidate) => candidate.variantId === String(variantId));
    if (!bonus) return { allowed: true };
    if (bonus.directAdd) return { allowed: true };

    const cart = await fetchCart();
    const existingLines = getBonusLines(cart, bonus);

    return {
      allowed: false,
      message: existingLines.length
        ? `${bonus.label} is already in your cart.`
        : 'Claim this retailer bonus from the retailer bonus section.',
    };
  };

  const handleFreeShippingRemove = (event) => {
    const button = event.target.closest('[data-retailer-free-shipping-remove]');
    if (!button) return;

    event.preventDefault();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    setFreeShippingClaimed(false)
      .catch((error) => {
        console.error('Kobu free shipping removal failed', error);
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  };

  window.KobuRetailerBonusCart = {
    claim: claimBonus,
    fetchCart,
    getBonusStates,
    getQualifyingSubtotal,
    isFreeShippingClaimed,
    isBonusLine,
    prepareProductAdd,
    removeFreeShipping: () => setFreeShippingClaimed(false),
    resolve: resolveBonusDefinitions,
    sync: syncBonuses,
  };

  document.addEventListener('click', handleCheckoutClick, true);
  document.addEventListener('submit', handleCheckoutSubmit, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cartData = event?.cartData && Array.isArray(event.cartData.items) ? event.cartData : null;

      if (event?.cartData && Array.isArray(event.cartData.items)) {
        updateBonusProductLinks(event.cartData);
      } else {
        fetchCart().then(updateBonusProductLinks).catch(() => {});
      }

      if (event?.source === 'retailer-bonus-cart') return;

      syncBonuses(cartData);
    });
  }

  const updateLinksOnLoad = () => {
    resolveBonusDefinitions()
      .then(fetchCart)
      .then((cart) => {
        updateBonusProductLinks(cart);
        return syncBonuses(cart);
      })
      .catch(() => {});
  };

  window.addEventListener('pageshow', updateLinksOnLoad);
  document.addEventListener('click', handleFreeShippingRemove);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateLinksOnLoad, { once: true });
  } else {
    updateLinksOnLoad();
  }
})();
